import { ReportColumn, ReportFilter, DateRangePreset } from '../types';

// CSV Export
interface CSVExportOptions {
  filename: string;
  columns: ReportColumn[];
  data: any[];
}

export const exportToCSV = async (options: CSVExportOptions): Promise<void> => {
  const { filename, columns, data } = options;
  
  // Build header row
  const headers = columns.map(col => `"${col.label}"`).join(',');
  
  // Build data rows
  const rows = data.map(item => {
    return columns.map(col => {
      let value = item[col.field];
      
      // Format value based on type
      if (value === null || value === undefined) {
        value = '';
      } else if (col.type === 'date' && value) {
        value = new Date(value).toLocaleDateString();
      } else if (col.type === 'currency') {
        value = Number(value).toFixed(2);
      } else if (col.type === 'percentage') {
        value = Number(value).toFixed(1) + '%';
      }
      
      // Escape quotes and wrap in quotes
      const stringValue = String(value).replace(/"/g, '""');
      return `"${stringValue}"`;
    }).join(',');
  });
  
  const csv = [headers, ...rows].join('\n');
  
  // Download file
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// PDF Export
interface PDFExportOptions {
  filename: string;
  title: string;
  columns: ReportColumn[];
  data: any[];
  summary: {
    totalRows: number;
    totals: Record<string, number>;
  };
  filters?: ReportFilter[];
  dateRange?: { start: Date; end: Date } | DateRangePreset;
}

export const exportToPDF = async (options: PDFExportOptions): Promise<void> => {
  const { filename, title, columns, data, summary, filters, dateRange } = options;
  
  // Dynamic import of jsPDF
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;
  
  const doc = new jsPDF({
    orientation: columns.length > 6 ? 'landscape' : 'portrait',
    unit: 'mm',
    format: 'letter',
  });
  
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  
  // Header
  doc.setFillColor(44, 62, 47); // #2C3E2F
  doc.rect(0, 0, pageWidth, 25, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.text(title, margin, 16);
  
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const dateStr = `Generated: ${new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  })}`;
  doc.text(dateStr, pageWidth - margin - doc.getTextWidth(dateStr), 16);
  
  let yPosition = 35;
  
  // Filters section
  if ((filters && filters.length > 0) || dateRange) {
    doc.setTextColor(44, 62, 47);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.text('Filters Applied:', margin, yPosition);
    yPosition += 5;
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(79, 66, 58); // #4F423A
    
    if (dateRange) {
      const rangeText = typeof dateRange === 'string' 
        ? `Date Range: ${dateRange.replace(/-/g, ' ')}`
        : `Date Range: ${new Date(dateRange.start).toLocaleDateString()} - ${new Date(dateRange.end).toLocaleDateString()}`;
      doc.text(`• ${rangeText}`, margin + 2, yPosition);
      yPosition += 4;
    }
    
    if (filters) {
      filters.forEach(filter => {
        doc.text(`• ${filter.field} ${filter.operator} "${filter.value}"`, margin + 2, yPosition);
        yPosition += 4;
      });
    }
    
    yPosition += 5;
  }
  
  // Data table
  const tableColumns = columns.map(col => ({
    header: col.label,
    dataKey: col.field,
  }));
  
  const tableData = data.map(item => {
    const row: Record<string, string> = {};
    columns.forEach(col => {
      let value = item[col.field];
      if (value === null || value === undefined) {
        row[col.field] = '-';
      } else if (col.type === 'date') {
        row[col.field] = new Date(value).toLocaleDateString();
      } else if (col.type === 'currency') {
        row[col.field] = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
      } else if (col.type === 'percentage') {
        row[col.field] = `${Number(value).toFixed(1)}%`;
      } else if (col.type === 'number') {
        row[col.field] = Number(value).toLocaleString();
      } else {
        row[col.field] = String(value);
      }
    });
    return row;
  });
  
  autoTable(doc, {
    startY: yPosition,
    head: [tableColumns.map(c => c.header)],
    body: tableData.map(row => tableColumns.map(c => row[c.dataKey])),
    margin: { left: margin, right: margin },
    styles: {
      fontSize: 9,
      cellPadding: 3,
      font: 'helvetica',
    },
    headStyles: {
      fillColor: [44, 62, 47],
      textColor: [255, 255, 255],
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [247, 247, 247],
    },
    didDrawPage: (data: any) => {
      // Footer
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Page ${data.pageNumber}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    },
  });
  
  // Summary section
  if (Object.keys(summary.totals).length > 0) {
    const finalY = (doc as any).lastAutoTable?.finalY || yPosition + 100;
    
    if (finalY + 30 > pageHeight - 20) {
      doc.addPage();
      yPosition = 25;
    } else {
      yPosition = finalY + 10;
    }
    
    doc.setFillColor(222, 219, 206); // #DEDBCE
    doc.roundedRect(margin, yPosition, pageWidth - (margin * 2), 25, 3, 3, 'F');
    
    doc.setTextColor(44, 62, 47);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', margin + 5, yPosition + 8);
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    let xPos = margin + 5;
    doc.text(`Total Records: ${summary.totalRows}`, xPos, yPosition + 17);
    xPos += 50;
    
    Object.entries(summary.totals).forEach(([field, total]) => {
      const col = columns.find(c => c.field === field);
      const label = col?.label || field;
      let formattedValue: string;
      
      if (col?.type === 'currency') {
        formattedValue = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(total);
      } else {
        formattedValue = total.toLocaleString();
      }
      
      const text = `Total ${label}: ${formattedValue}`;
      if (xPos + doc.getTextWidth(text) > pageWidth - margin) {
        xPos = margin + 5;
        yPosition += 5;
      }
      doc.text(text, xPos, yPosition + 17);
      xPos += doc.getTextWidth(text) + 15;
    });
  }
  
  // Save
  doc.save(filename);
};
